import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { mutate } from "swr";

import { enterCompetition, patchProfile } from "../../../../../api";
import useAsyncMemo from "../../../../../hooks/useAsyncMemo";
import useCompetition, {
  useLeagueEntry,
} from "../../../../../hooks/useCompetition";
import useCurrentUser from "../../../../../hooks/useCurrentUser";
import useDebounce from "../../../../../hooks/useDebounce";
import useErrorAlert from "../../../../../hooks/useErrorAlert";
import getCanEnterCompetition from "../../../../../utils/getCanEnterCompetition";
import getCategoryFromPrevious from "../../../../../utils/getCategoryFromPrevious";
import getGenericErrorInfo from "../../../../../utils/getGenericErrorInfo";
import { useEntryAuthWall } from "../../hooks/useAuthWall";
import useProfileForm, {
  getPrefillDataForCompetitorNumber,
  ProfileValues,
} from "../../hooks/useProfileForm";

export default function useProfileScreen() {
  useEntryAuthWall();

  const params = useParams();
  const { profile } = useCurrentUser();
  const { competition, isValidating: compIsValidating } = useCompetition(
    params.competitionUUID
  );

  const { entry: leagueEntry, isValidating: leagueEntryIsValidating } =
    useLeagueEntry({
      leagueUUID: competition?.league?.uuid,
      userUUID: profile?.uuid,
    });

  const isLeagueEntryLoading =
    !leagueEntry &&
    (competition || compIsValidating) &&
    leagueEntryIsValidating;

  const navigate = useNavigate();
  const { setError } = useErrorAlert();

  const onSubmit = async (values: ProfileValues) => {
    if (!competition || !profile) {
      return;
    }

    try {
      const updatedProfile = await patchProfile({
        firstName: values.firstName,
        lastName: values.lastName,
        profilePicture: values.profilePicture,
      });
      mutate("/profiles/me", updatedProfile);

      await enterCompetition({
        competitionUUID: competition.uuid,
        userUUID: "me",
        categoryUUID: values.category,
        competitorNumber: values.competitorNumber || undefined,
      });

      mutate(`/competitions/${competition.uuid}`);
      mutate(`/competitions/${competition.uuid}/entries`);
      mutate(`/competitions/${competition.uuid}/entries/${profile.uuid}`);

      navigate(`/${competition.uuid}`);
    } catch (error) {
      setError(getGenericErrorInfo(error));
    }
  };

  const [canEnterCompetition, canEnterCompetitionReason] = competition
    ? getCanEnterCompetition(competition)
    : ([undefined, ""] as const);

  const previousCategoryUUID = useMemo(
    () => leagueEntry && getCategoryFromPrevious(leagueEntry.entries),
    [leagueEntry]
  );

  const { schema, initialValues } = useProfileForm({
    profile,
    competition,
    previousCategoryUUID,
  });

  const [competitorNumber, setCompetitorNumber] = useState("");
  const competitorNumberDebounced = useDebounce(competitorNumber, 500);

  const [existingCompetitorData, isCheckingExistingCompetitorData] =
    useAsyncMemo(
      () =>
        competition
          ? getPrefillDataForCompetitorNumber(
              competition.uuid,
              competitorNumberDebounced
            )
          : Promise.resolve(null),
      [competitorNumberDebounced, competition]
    );

  return {
    profile,
    competition,
    schema,
    canChangeCategory: previousCategoryUUID === undefined,
    onSubmit,
    initialValues,
    canEnterCompetition,
    canEnterCompetitionReason,
    isLeagueEntryLoading,
    setCompetitorNumber,
    existingCompetitorData,
    isCheckingExistingCompetitorData,
  };
}
